import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Info, Trophy } from 'lucide-react';
import { cn, formatScore, scoreColor } from '@/lib/utils';

interface RankingItem {
    position?: number | null;
    score?: number | null;
    tokens?: number | null;
    status?: string | null;
    runner?: {
        name?: string | null;
        result?: number | null;
    } | null;
}

interface BonusItem {
    type?: string | null;
    points?: number | null;
    gender?: string | null;
}

interface BetScoreBreakdownProps {
    rankings: RankingItem[];
    bonuses?: BonusItem[];
}

const PREVIEW_COUNT = 3;

export function BetScoreBreakdown({ rankings, bonuses }: BetScoreBreakdownProps) {
    const { t } = useTranslation('bets');
    const [expanded, setExpanded] = useState(false);

    const sorted = [...rankings].sort((a, b) => {
        // DNS at the end
        if (a.status === 'DNS' && b.status !== 'DNS') return 1;
        if (b.status === 'DNS' && a.status !== 'DNS') return -1;
        const posA = a.position ?? Infinity;
        const posB = b.position ?? Infinity;
        if (posA === 0 && posB !== 0) return 1;
        if (posB === 0 && posA !== 0) return -1;
        return posA - posB;
    });

    const hasMore = sorted.length > PREVIEW_COUNT;
    const visible = expanded ? sorted : sorted.slice(0, PREVIEW_COUNT);
    const hiddenCount = sorted.length - PREVIEW_COUNT;
    const activeBonuses = (bonuses ?? []).filter(b => b.type && (b.points ?? 0) > 0);

    return (
        <div className="mt-3 rounded-xl bg-slate-50 overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-[1fr_60px_60px_70px] gap-1 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <span>{t('list.breakdown.runner')}</span>
                <span className="text-center">{t('list.breakdown.predicted')}</span>
                <span className="text-center">{t('list.breakdown.actual')}</span>
                <Link to="/scoring" className="text-right flex items-center justify-end gap-0.5 hover:text-indigo-500 transition-colors">
                    {t('list.breakdown.points')}
                    <Info size={10} />
                </Link>
            </div>

            {/* Rows */}
            {visible.map((ranking, i) => {
                const isDns = ranking.status === 'DNS';

                const predicted = ranking.position === 0
                    ? t('list.breakdown.predictedDnf')
                    : t('list.breakdown.predictedPos', { position: ranking.position });

                const actual = isDns
                    ? t('dns.badge')
                    : ranking.runner?.result == null
                        ? '—'
                        : ranking.runner.result === 0
                            ? t('list.breakdown.actualDnf')
                            : t('list.breakdown.actualPos', { position: ranking.runner.result });

                const score = isDns ? null : (ranking.score ?? 0);
                const isDoubled = (ranking.tokens ?? 1) > 1;

                return (
                    <div
                        key={ranking.runner?.name ?? i}
                        className={cn(
                            "grid grid-cols-[1fr_60px_60px_70px] gap-1 px-3 py-2 text-sm",
                            i % 2 === 1 && "bg-slate-100/50",
                            isDns && "opacity-50"
                        )}
                    >
                        <span className="text-slate-700 font-medium truncate flex items-center gap-1">
                            {ranking.runner?.name}
                            {isDoubled && (
                                <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-black bg-amber-100 text-amber-700" title={t('doubleDown.tooltip')}>
                                    {t('doubleDown.badge')}
                                </span>
                            )}
                            {isDns && (
                                <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-black bg-slate-200 text-slate-500" title={t('dns.tooltip')}>
                                    {t('dns.badge')}
                                </span>
                            )}
                        </span>
                        <span className="text-center text-slate-500">{predicted}</span>
                        <span className={cn("text-center text-slate-500", isDns && "font-semibold text-slate-400")}>{actual}</span>
                        <span className={cn(
                            "text-right font-bold",
                            score === null ? "text-slate-300" : scoreColor(score)
                        )}>
                            {score === null ? '—' : formatScore(score)}
                        </span>
                    </div>
                );
            })}

            {/* Bonuses */}
            {activeBonuses.length > 0 && (
                <div className="border-t border-slate-200 px-3 py-2">
                    {activeBonuses.map((bonus) => (
                        <div key={bonus.type} className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-1 text-amber-700 font-medium">
                                <Trophy size={12} />
                                {t(`bonus.${bonus.type}`)}
                            </span>
                            <span className="font-bold text-emerald-600">{formatScore(bonus.points ?? 0)}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Toggle */}
            {hasMore && (
                <button
                    onClick={() => setExpanded(!expanded)}
                    aria-expanded={expanded}
                    aria-label={expanded ? t('list.showLess') : t('list.showMore', { count: hiddenCount })}
                    className="flex items-center gap-1 w-full px-3 py-2 text-xs font-semibold text-slate-400 hover:text-slate-600 hover:bg-slate-100/50 transition-colors"
                >
                    {expanded ? (
                        <>
                            <ChevronUp size={14} />
                            {t('list.showLess')}
                        </>
                    ) : (
                        <>
                            <ChevronDown size={14} />
                            {t('list.showMore', { count: hiddenCount })}
                        </>
                    )}
                </button>
            )}
        </div>
    );
}
